import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import ChevronLeftLargeIcon from '@atlaskit/icon/glyph/chevron-left-large';
import SearchIcon from '@atlaskit/icon/glyph/search';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { TRACK_EVENT_TYPE } from '../../../../common/constants';
import type { CommandInternal, ListedCommand } from '../../../../common/types';
import { CommandPaletteSearchInput } from '../../../../common/ui/command-palette-search-dropdown/command-palette-search-input';
import { components } from '../../../../common/ui/components';
import { OutlinedLozenge, SearchLozenge } from '../../../../common/ui/custom-lozenge';
import {
	getAnalyticsAttributesFromResult,
	getQueryHash,
	getRegistryData,
	getWordCount,
	isSecondaryTrigger,
} from '../../../../common/utils';
import { getActiveCommandList } from '../../../../common/utils/get-active-command-list';
import { useCommandPalette } from '../../../../controllers';

import {
	COMMAND_MODE_TEST_ID,
	COMMAND_BACK_PARENT_TEST_ID,
	SPINNER_CONTAINER_TEST_ID,
	SEARCH_LOZENGE_TEST_ID,
} from './constants';
import messages from './messages';
import type { HeaderProps } from './types';

export const Header = ({
	defaultPlaceholder,
	results,
	onAnalytics: onAnalyticsOld,
	activeCommand,
}: HeaderProps) => {
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [
		{
			search,
			forceSearchFocus,
			isOpen,
			expandedCommand,
			activeIndex,
			loadingList,
			parentCommand,
			onAnalytics,
			registry,
			activeRegistry,
		},
		{ handleSearch, goBackLevel, forceFocusSearch, executeCommand, setFocusInput },
	] = useCommandPalette();

	const textFieldRef = useRef<HTMLInputElement>();

	const onFocusInput = (isFocused: boolean) => () => {
		setFocusInput(isFocused);
	};

	const onInputKeyPress = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Backspace' && search === '') {
				goBackLevel(false, {
					createAnalyticsEvent,
					onAnalyticsOld,
					method: 'keyboard',
				});
			}
			if (event.key === 'Enter') {
				event.preventDefault();
				const listed: ListedCommand | undefined = results.find(
					(result): result is ListedCommand => !result.isGroup && result.index === activeIndex,
				);

				if (listed) {
					executeCommand(
						listed.command,
						{
							method: 'keyboard',
							createAnalyticsEvent,
							onAnalyticsOld,
							attributes: {
								...getAnalyticsAttributesFromResult(listed),
								...getRegistryData(registry, activeRegistry, listed.command.id),
							},
						},
						isSecondaryTrigger(event),
					);
				}
			}
		},
		[
			search,
			goBackLevel,
			createAnalyticsEvent,
			onAnalyticsOld,
			results,
			activeIndex,
			executeCommand,
			registry,
			activeRegistry,
		],
	);

	const onTypeAnalytics = useCallback(
		(query: string) => {
			if (ff('compal-refactor-analytics') && onAnalytics && createAnalyticsEvent) {
				const event = createAnalyticsEvent({});
				onAnalytics(event, TRACK_EVENT_TYPE, {
					action: 'entered',
					actionSubject: 'text',
					actionSubjectId: 'commandPaletteSearch',
					attributes: {
						wordCount: getWordCount(query),
						queryLength: query.length,
						queryHash: getQueryHash(query),
						queryVersion: -1, // This is done to prevent the quickSearch queryVersion being sent which may be confusing, something to reimplement later
					},
				});
			} else if (!ff('compal-refactor-analytics') && onAnalyticsOld && createAnalyticsEvent) {
				const event = createAnalyticsEvent({
					__standardize: true,
				});
				onAnalyticsOld(event, TRACK_EVENT_TYPE, 'text entered', 'commandPaletteSearch', {
					wordCount: getWordCount(query),
					queryLength: query.length,
					queryHash: getQueryHash(query),
					queryVersion: -1, // This is done to prevent the quickSearch queryVersion being sent which may be confusing, something to reimplement later
				});
			}
		},
		[createAnalyticsEvent, onAnalytics, onAnalyticsOld],
	);

	const onTypeAnalyticsDebounced = useMemo(() => debounce(onTypeAnalytics, 400), [onTypeAnalytics]);

	useEffect(
		() => () => {
			onTypeAnalyticsDebounced?.cancel?.();
		},
		[onTypeAnalyticsDebounced],
	);

	const onTypeSearch = useCallback(
		(ev: React.ChangeEvent<HTMLInputElement>) => {
			const searchValue = ev.target.value;
			handleSearch(searchValue);
			onTypeAnalyticsDebounced(searchValue);
		},
		[handleSearch, onTypeAnalyticsDebounced],
	);

	const handleBackButton = useCallback(() => {
		goBackLevel(false, {
			createAnalyticsEvent,
			onAnalyticsOld,
			method: 'mouse',
		});
	}, [createAnalyticsEvent, goBackLevel, onAnalyticsOld]);

	useEffect(() => {
		if (textFieldRef.current && textFieldRef.current.value !== search) {
			textFieldRef.current.value = search;
		}
	}, [search]);

	useEffect(() => {
		if (textFieldRef.current && forceSearchFocus) {
			textFieldRef.current.focus();
			forceFocusSearch(false);
		}
	}, [forceSearchFocus, forceFocusSearch]);

	useEffect(() => {
		if (isOpen && textFieldRef.current) {
			textFieldRef.current.focus();
		}
	}, [isOpen]);

	const renderLozenge = useCallback((command: CommandInternal) => {
		if (command.mode) {
			return (
				<OutlinedLozenge key={command.id} data-testid={COMMAND_MODE_TEST_ID}>
					&nbsp;{command.mode}&nbsp;
				</OutlinedLozenge>
			);
		}

		const searchLozengeName = command.searchLozengeName || command.name;
		return (
			<SearchLozenge key={command.id} data-testid={SEARCH_LOZENGE_TEST_ID}>
				{searchLozengeName}
			</SearchLozenge>
		);
	}, []);

	const elemBeforeInput = useMemo(() => {
		if (!expandedCommand)
			return <SearchIcon label="" primaryColor={token('color.icon.subtle', colors.N700)} />;

		return (
			<ElemBeforeInputWrapper>
				<Tooltip content={formatMessage(messages.goBack)}>
					<BackButton
						testId={COMMAND_BACK_PARENT_TEST_ID}
						appearance="subtle"
						onClick={handleBackButton}
					>
						<ChevronLeftLargeIcon
							size="medium"
							label={formatMessage(messages.goBack)}
							primaryColor={token('color.icon.subtle', colors.N700)}
						/>
					</BackButton>
				</Tooltip>
				<LozengeContainer>
					{ff('compal-single-lozenge_6cjf0')
						? renderLozenge(expandedCommand)
						: [...parentCommand, expandedCommand].map((command) => renderLozenge(command))}
				</LozengeContainer>
			</ElemBeforeInputWrapper>
		);
	}, [expandedCommand, formatMessage, handleBackButton, parentCommand, renderLozenge]);

	const elemAfterInput = useMemo(() => {
		if (expandedCommand && loadingList) {
			return (
				<SpinnerContainer data-testid={SPINNER_CONTAINER_TEST_ID}>
					<Spinner size="small" />
				</SpinnerContainer>
			);
		}

		return null;
	}, [expandedCommand, loadingList]);

	const placeholder = useMemo(() => {
		if (expandedCommand) return expandedCommand.placeholder || expandedCommand.name;
		return defaultPlaceholder;
	}, [expandedCommand, defaultPlaceholder]);

	const ModalHeader = useMemo(
		() =>
			ff('compal-issue-actions-very-high-priority_f5w5r')
				? getActiveCommandList(expandedCommand)?.components?.Header || components.Header
				: components.Header,
		[expandedCommand],
	);

	return (
		<ModalHeader>
			<CommandPaletteSearchInput
				aria-label={placeholder}
				placeholder={placeholder}
				onKeyDown={onInputKeyPress}
				elemBeforeInput={elemBeforeInput}
				elemAfterInput={elemAfterInput}
				onChange={onTypeSearch}
				ref={textFieldRef}
				onFocus={onFocusInput(true)}
				onBlur={onFocusInput(false)}
				activeId={activeCommand?.id}
			/>
		</ModalHeader>
	);
};

export default Header;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BackButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	width: '24px !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	height: '24px !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	lineHeight: '24px !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	padding: '0 !important',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	svg: {
		marginLeft: '-5px',
	},

	'&:hover, &:focus': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		svg: {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			color: `${token('color.icon', colors.N800)}`,
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerContainer = styled.div({
	width: '24px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ElemBeforeInputWrapper = styled.div({
	display: 'flex',
	gap: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.050', '4px'),
	marginRight: token('space.025', '2px'),
});
